import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, Wallet, ChevronLeft } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR } from "../../lib/utils.fmt";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import LoginRequired from "./LoginRequired";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function Loyalty() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/api/customer/loyalty/me").then((r) => setData(r.data)).catch((e) => {
    if (e.response?.status === 404) setData({ notEnabled: true });
  });
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <LoginRequired title="Sign in to view your loyalty points"/>;
  if (!data) return <LoadingState fullHeight testId="loyalty-loading" />;
  if (data.notEnabled) return (
    <EmptyState icon={Heart} title="Loyalty isn't enabled" description="This store hasn't turned on loyalty points yet." fullHeight testId="loyalty-disabled" />
  );

  const { points, redeem_value_inr, settings, ledger } = data;
  const canRedeem = points >= settings.min_redeem_points && settings.is_enabled;

  const redeem = async () => {
    setBusy(true);
    try {
      const { data: out } = await api.post("/api/customer/loyalty/redeem", { points });
      toast.success(`Redeemed ${points} pts → ${formatINR(out.wallet_credited)} added to wallet`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Redemption failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="page-enter pb-16" data-testid="loyalty-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/me" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5"/>
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <Heart className="w-6 h-6 text-brand-clay"/> Loyalty
          </h1>
        </div>
      </header>

      <main className="px-5 space-y-4">
        <div className="rounded-3xl bg-gradient-to-br from-brand-clay to-brand-sand text-white p-6" data-testid="points-card">
          <div className="text-xs uppercase tracking-wider font-bold text-white/70">Your points</div>
          <div className="font-heading text-5xl font-extrabold tracking-tight mt-1" data-testid="points-balance">{points}</div>
          <div className="text-sm text-white/80 mt-1">≈ {formatINR(redeem_value_inr)} when redeemed to wallet</div>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="redeem-card">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Redeem</div>
          <div className="text-sm text-neutral-700">
            Minimum <b>{settings.min_redeem_points} points</b> · 1 pt = {formatINR(settings.redeem_rate_paise / 100)}
          </div>
          <Button onClick={redeem} disabled={!canRedeem || busy}
                  className="w-full h-12 rounded-2xl bg-brand-ink text-white font-extrabold mt-4"
                  data-testid="redeem-btn">
            <Wallet className="w-4 h-4 mr-1.5"/>
            {canRedeem ? `Redeem all ${points} pts for ${formatINR(redeem_value_inr)}` : `Need ${Math.max(0, settings.min_redeem_points - points)} more pts`}
          </Button>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-5">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-3 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5"/> Recent activity
          </div>
          {ledger.length === 0 ? (
            <EmptyState icon={Sparkles} size="sm" title="No points history yet"
                        description="Complete a delivery to earn your first points!"
                        className="py-6" testId="ledger-empty" />
          ) : (
            <ul className="space-y-2">
              {ledger.map((l) => (
                <li key={l.id} className="flex items-center justify-between text-sm" data-testid={`ledger-row-${l.id}`}>
                  <div>
                    <div className="font-bold capitalize">{l.type}</div>
                    <div className="text-xs text-neutral-500">{l.description || "—"}</div>
                  </div>
                  <div className={`font-mono font-extrabold ${l.delta > 0 ? "text-brand-leaf" : "text-brand-clay"}`}>
                    {l.delta > 0 ? "+" : ""}{l.delta} pt
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
